import { useState, useRef } from "react";
import { ChevronsRight, ChevronsDown } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
/* ================= FAQ DATA ================= */

const faqs = [
  {
    question: "How long does the student visa process usually take?",
    answer:
      "The student visa process typically takes 2–6 weeks, depending on the country, university, and documentation completeness.",
  },
  {
    question: "Do you assist with scholarship applications as well?",
    answer:
      "Yes, we help students identify suitable scholarships and prepare strong applications to improve approval chances.",
  },
  {
    question: "Will you guide me in preparing for the visa interview?",
    answer:
      "Absolutely. We provide complete visa interview preparation, including mock interviews and expert tips.",
  },
  {
    question: "Do you offer post-arrival support for students?",
    answer:
      "Yes, we support students even after arrival, including accommodation guidance and settling assistance.",
  },
];

/* ================= MODERN ANIMATION VARIANTS ================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.25,
      delayChildren: 0.2,
    },
  },
};

const softFadeUp = {
  hidden: {
    opacity: 0,
    y: 12,
    scale: 0.98,
    filter: "blur(6px)",
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const faqCard = {
  hidden: {
    opacity: 0,
    y: 16,
    scale: 0.97,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 1.3,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

/* ================= COMPONENT ================= */

export default function Info() {
  const [openIndex, setOpenIndex] = useState(1);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section ref={ref} className="w-full bg-white py-16 sm:py-20 lg:py-24">
      <div
        className="max-w-[1300px] mx-auto px-5 sm:px-8 lg:px-10
        grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start"
      >
        {/* ================= LEFT CONTENT ================= */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.span
            variants={softFadeUp}
            className="inline-block mb-3 px-4 py-1 rounded-full
            bg-blue-50 text-blue-600 text-sm font-medium"
          >
            Visa FAQs
          </motion.span>

          <motion.h2
            variants={softFadeUp}
            className="text-[30px] sm:text-[36px] lg:text-[42px]
            font-extrabold leading-[1.15] text-[#0b1b3f] mb-5"
          >
            Got Questions? 
            <br />
            We’ve Got Clear Answers
          </motion.h2>

          <motion.p
            variants={softFadeUp}
            className="text-gray-500 text-[14px] sm:text-[15px]
            max-w-md mb-6"
          >
            We understand students often have many questions about studying
            abroad. Our experts provide clear, helpful answers to the most
            common concerns.
          </motion.p>
          <Link to="/contact">
          <motion.button
            variants={softFadeUp}
            className="flex items-center gap-4 border bg-blue-700 border-gray-300
            rounded-full pl-6 pr-2 py-2 text-sm font-semibold text-white
            hover:bg-black hover:text-white-700 transition-colors duration-300"
          >
            CONTACT US
            <span
              className="w-10 h-10 flex items-center justify-center
              rounded-full bg-yellow-500 text-white"
            >
              <ChevronsRight size={18} />
            </span>
          </motion.button>
          </Link>
        </motion.div>

        {/* ================= RIGHT FAQ ================= */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="bg-[#f9fbff] rounded-2xl p-4 sm:p-6 space-y-4"
        >
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <motion.div
                key={index}
                variants={faqCard}
                className="bg-white rounded-xl px-5 sm:px-6 py-5
                shadow-[0_10px_30px_rgba(0,0,0,0.08)]
                hover:shadow-[0_14px_40px_rgba(0,0,0,0.12)]
                transition-shadow duration-300"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <span className="text-[14px] sm:text-[15px] font-semibold text-[#0b1b3f]">
                    {faq.question}
                  </span>

                  {isOpen ? (
                    <ChevronsDown className="text-red-500 shrink-0" size={20} />
                  ) : (
                    <ChevronsRight
                      className="text-blue-600 shrink-0 transition-transform duration-300"
                      size={20}
                    />
                  )}
                </button>

                {/* ================= ANSWER ================= */}
                <motion.div
                  initial={false}
                  animate={isOpen ? "open" : "collapsed"}
                  variants={{
                    open: {
                      height: "auto",
                      opacity: 1,
                      transition: {
                        height: {
                          duration: 0.6,
                          ease: [0.16, 1, 0.3, 1],
                        },
                        opacity: {
                          duration: 0.4,
                          delay: 0.15,
                        },
                      },
                    },
                    collapsed: {
                      height: 0,
                      opacity: 0,
                      transition: {
                        height: {
                          duration: 0.5,
                          ease: [0.16, 1, 0.3, 1],
                        },
                        opacity: {
                          duration: 0.2,
                        },
                      },
                    },
                  }}
                  className="overflow-hidden"
                >
                  <p className="mt-4 text-[14px] leading-relaxed text-gray-500">
                    {faq.answer}
                  </p>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
